import React from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { CheckCircle } from 'react-feather';
import { segmentationBuilderApi } from './segmentation-api';
import { EDACharts } from './Segmentation.EDA.Charts';

interface SegmentationEDAProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationEDA;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #323232;
  margin: 0;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #e5e5e5;
  background: #fafafa;
`;

const CardBody = styled.div`
  padding: 18px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e5e5e5;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #626262;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #28aae1;
`;

const NarrativeBox = styled.div`
  padding: 16px;
  background: #f0f7ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  color: #1d4ed8;
  font-size: 14px;
  line-height: 1.6;
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #28aae1;
  color: #ffffff;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background: #1d94c7;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e5e5e5;
    color: #adadad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;


export function SegmentationEDA({ queryId, queryIdentifier, value }: SegmentationEDAProps) {
  const [continuing, setContinuing] = React.useState(false);

  const handleContinue = async () => {
    setContinuing(true);
    try {
      await segmentationBuilderApi.continueFromEDA(queryIdentifier);
    } catch (e) {
      console.error('Failed to continue:', e);
    } finally {
      setContinuing(false);
    }
  };
  const eda = value.eda;

  if (!eda) {
    return (
      <Wrapper>
        <Card>
          <CardBody>
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#adadad' }}>
              Loading exploratory data analysis...
            </div>
          </CardBody>
        </Card>
      </Wrapper>
    );
  }

  const { eda_report } = eda;
  const { dataset_summary, hcp_level } = eda_report;

  return (
    <Wrapper>
      <Card>
        <CardHeader>
          <SectionTitle>Dataset Summary</SectionTitle>
        </CardHeader>
        <CardBody>
          <StatsGrid>
            <StatCard>
              <StatLabel>Total HCPs</StatLabel>
              <StatValue>{dataset_summary.total_hcps.toLocaleString()}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Total Records</StatLabel>
              <StatValue>{dataset_summary.total_rows.toLocaleString()}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Time Periods</StatLabel>
              <StatValue>{dataset_summary.time_periods.length}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Segmentable HCPs</StatLabel>
              <StatValue>
                {hcp_level.segmentable_hcps
                  ? hcp_level.segmentable_hcps.count.toLocaleString()
                  : hcp_level.count.toLocaleString()}
              </StatValue>
            </StatCard>
          </StatsGrid>
        </CardBody>
      </Card>

      {eda.narrative && (
        <Card>
          <CardHeader>
            <SectionTitle>Analysis Summary</SectionTitle>
          </CardHeader>
          <CardBody>
            <NarrativeBox>{eda.narrative}</NarrativeBox>
          </CardBody>
        </Card>
      )}

      {eda.charts && Object.keys(eda.charts).length > 0 && (
        <Card>
          <CardHeader>
            <SectionTitle>Data Visualizations</SectionTitle>
          </CardHeader>
          <CardBody>
            <EDACharts charts={eda.charts} />
          </CardBody>
        </Card>
      )}

      <ContinueButton type="button" onClick={handleContinue} disabled={continuing}>
        <CheckCircle size={18} />
        {continuing ? 'Continuing...' : 'Continue to Strategy Selection'}
      </ContinueButton>
    </Wrapper>
  );
}
